import { Download, FileSpreadsheet, Loader2, RotateCw, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  NODE_NAME_I18N_CONTEXT,
  NODE_STATUS_I18N_CONTEXT,
  type NodeSpec,
  type NodeStatus,
  type TabSpec,
} from "./nodeSpec";

interface NodeStageCardProps {
  node: NodeSpec;
  status: NodeStatus;
  elapsed?: string;
  summary?: string;
  tabs: TabSpec[];
  activeTab: string;
  onTabChange: (id: string) => void;
  onCsv?: () => void;
  downloadingCsv?: boolean;
  onDownloadXlsx?: () => void;
  downloadingXlsx?: boolean;
  onRules?: () => void;
  onRerun?: () => void;
  children: React.ReactNode;
}

const STATUS_LABEL: Record<NodeStatus, string> = {
  done: "done",
  active: "running",
  pending: "pending",
  error: "errored",
  paused: "awaiting input",
  // #854: a run terminou; quem está pendente é a decisão de escopo, não a
  // execução. Rótulo diferente do `paused` de propósito.
  awaiting: "awaiting your decision",
  loading: "loading",
};

export function NodeStageCard({
  node,
  status,
  elapsed,
  summary,
  tabs,
  activeTab,
  onTabChange,
  onCsv,
  downloadingCsv = false,
  onDownloadXlsx,
  downloadingXlsx = false,
  onRules,
  onRerun,
  children,
}: NodeStageCardProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-[#e8ebf0] rounded-[10px] shadow-[0_1px_2px_rgba(8,37,76,0.04)] overflow-hidden flex flex-col flex-1">
      <div className="px-5 py-3.5 border-b border-[#f2f4f7] flex justify-between items-center gap-3 flex-shrink-0">
        <div>
          <div className="font-mono text-[9px] text-[#ffc83d] tracking-[0.22em] uppercase font-semibold mb-1 flex items-center gap-2">
            <StatusDot status={status} />
            <span>
              {t("Node {{num}}", { num: node.num })} · {node.phase} ·{" "}
              {t(STATUS_LABEL[status], { context: NODE_STATUS_I18N_CONTEXT })}
            </span>
            {elapsed && elapsed !== "—" && (
              <span className="text-[#7b8799] font-medium">· {elapsed}</span>
            )}
          </div>
          <h2 className="font-brand font-bold text-lg text-[#08254c] m-0 tracking-[-0.01em] flex items-baseline gap-2.5">
            {t(node.name, { context: NODE_NAME_I18N_CONTEXT })}
            {summary && (
              <span className="font-mono text-[11px] text-[#7b8799] font-normal tracking-normal normal-case">
                {summary}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-1.5">
          {onDownloadXlsx && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[10px] gap-1.5 px-2.5"
              onClick={onDownloadXlsx}
              disabled={downloadingXlsx}
              aria-busy={downloadingXlsx}
              title={downloadingXlsx ? t("Downloading BOM...") : t("Download BOM as Excel")}
            >
              {downloadingXlsx ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-3 h-3" />
              )}
              {t("XLSX")}
            </Button>
          )}
          {onCsv && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[10px] gap-1.5 px-2.5"
              onClick={onCsv}
              disabled={downloadingCsv}
              aria-busy={downloadingCsv}
              title={
                downloadingCsv
                  ? t("Downloading...")
                  : t(
                      "Only the bom_summary sheet (totals and final lines). For the full Excel report with all sheets, use XLSX.",
                    )
              }
            >
              {downloadingCsv ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
              {t("CSV (summary)")}
            </Button>
          )}
          {onRules && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[10px] gap-1.5 px-2.5"
              onClick={onRules}
            >
              <SlidersHorizontal className="w-3 h-3" /> {t("Rules")}
            </Button>
          )}
          {onRerun && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={onRerun}
              title={t("Re-run from this node")}
              aria-label={t("Re-run from this node")}
            >
              <RotateCw className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      <div
        className="flex gap-0.5 px-4 bg-[#fafbfc] border-b border-[#e8ebf0] overflow-x-auto flex-shrink-0"
        role="tablist"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "px-3 py-2.5 text-[11.5px] font-medium cursor-pointer border-b-2 border-transparent flex items-center gap-1.5 whitespace-nowrap transition-colors",
                isActive
                  ? "text-[#08254c] font-semibold border-b-[#ffc83d] bg-white"
                  : "text-[#7b8799] hover:text-[#08254c]",
              )}
            >
              {t(tab.label)}
              {tab.count != null && (
                <span
                  className={cn(
                    "font-mono text-[9px] px-1.5 py-px rounded-full font-semibold",
                    isActive
                      ? "bg-[rgba(255,200,61,0.18)] text-[#b57a00]"
                      : "bg-[#eef3f9] text-[#596b85]",
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 px-5 py-4">{children}</div>
    </div>
  );
}

function StatusDot({ status }: { status: NodeStatus }) {
  // `paused` shares the gold accent with `active` but without the pulse —
  // signals "needs input" without competing with the active-node spotlight.
  const cls =
    status === "done"
      ? "bg-[#27a155] shadow-[0_0_6px_#27a155]"
      : status === "active" || status === "loading"
        ? "bg-[#ffc83d] shadow-[0_0_8px_#ffc83d] mg-pulse motion-reduce:animate-none"
        : status === "paused"
          ? "bg-[#ffc83d] shadow-[0_0_8px_#ffc83d] ring-2 ring-[#ffc83d]/40 ring-offset-0"
          : status === "error"
            ? "bg-[#d93636] shadow-[0_0_6px_#d93636]"
            : "bg-[#d4dae3]";
  return <span className={cn("inline-block w-[7px] h-[7px] rounded-full", cls)} />;
}
